import * as types from '../../actions/actiontypes';
import initialstate from '../initialstate';
import _ from 'lodash';
import {entities, HttpStatusCodes, ViewKeys} from '../../util/uiconstants';
import {isApiResponseOk, normalize, normalizedDelete, normalizedUpdate} from '../../util/utils';

function mapAccount(account) {
	const mappedAccount = {...account};

	if (account.assertions) {
		mappedAccount.assertions = account?.assertions?.map((assertion) => assertion);
	}

	if (account.entities) {
		mappedAccount.entities = account?.entities?.map((entity) => entity.id);
	}

	return mappedAccount;
}

/**
 * Deletes an account from a normalized state.
 * @param {Object} state Normalized accounts state.
 * @param {number} accountId Account id.
 * @returns	{Object} Updated normalized accounts state.
 */
function deleteAccount(state, accountId) {
	return normalizedDelete(state, accountId);
}

export function accountsReducer(state = initialstate.accounts, action) {
	let newState;

	switch (action.type) {
		case types.GET_ACCOUNTS:
			if (action.response && action.response.data) {
				newState = {...state};

				const accountList = normalize(action.response.data.map((account) => mapAccount(account)));
				accountList.pages = action.response.pages;
				accountList.page = action.response?.page;
				accountList.totalEntityCount = action.response.totalEntityCount;

				newState = {...accountList};

				return newState;
			}
			return state;
		case types.DELETE_ACCOUNT: {
			if (isApiResponseOk(action) || action.error.response.status === HttpStatusCodes.NotFound) {
				newState = {...state};

				// Delete the account from both manageAccounts and root slices
				if (newState.manageAccounts) {
					newState.manageAccounts = deleteAccount(newState.manageAccounts, action.accountId);
				}

				newState = deleteAccount(newState, action.accountId);

				return newState;
			}

			return state;
		}
		case types.CREATE_MANAGE_ACCOUNT:
		case types.CREATE_ACCOUNT: {
			if (isApiResponseOk(action) && action.response) {
				const newAccount = mapAccount(action.response);

				newState = normalize([newAccount], undefined, state);

				return newState;
			}
			return state;
		}
		case types.UPDATE_ACCOUNT:
			if (isApiResponseOk(action)) {
				newState = {...state};
				// Update account in both manageAccount and root slices:
				const accountPayload = mapAccount(action.response);

				if (newState.manageAccounts) {
					newState.manageAccounts = normalizedUpdate(accountPayload.id, accountPayload, newState.manageAccounts);
				}

				newState = normalizedUpdate(accountPayload.id, accountPayload, newState);

				return newState;
			}

			return state;
		case types.GET_ACCOUNT_BY_ID: {
			if (isApiResponseOk(action)) {
				newState = {...state};
				const accountPayload = mapAccount(action.response);

				if (accountPayload?.entities === null) {
					accountPayload.entities = newState?.byId[action?.response?.id]?.entities;
				}
				newState = normalize([accountPayload], undefined, newState);

				return newState;
			}

			return state;
		}

		case types.SAVE_BATCH_HELIX_CANVAS_ACCOUNT_RELATIONS:
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const returnedAccountList = action.response;
				const sentAccountList = state.sentAccounts;

				if (returnedAccountList.length < sentAccountList.length) {
					newState = {
						..._.cloneDeep(state),
						hasHelixAccountMappingsErrors: true
					};
					return newState;
				}
				return state;
			}
			return state;
		// eslint-disable-next-line no-case-declarations
		case types.SAVE_SENT_ASSOCIATED_HELIX_ACCOUNTS: {
			return {...state, sentAccounts: action.accountList};
		}
		case types.DELETE_DOCUMENT_BY_ID: {
			if (action.viewKey === ViewKeys.Account && !action.error) {
				return {
					...state,
					byId: {
						...state.byId,
						[action.entityUId]: {
							...state.byId[action.entityUId],
							relatedDocuments: state.byId[action.entityUId]?.relatedDocuments?.filter(
								(doc) => doc.id !== action.documentId
							)
						}
					}
				};
			}
			return state;
		}
		case types.RELATE_ENTITY_ACCOUNT: {
			const entiytIds = state.byId[action.accountId].entities;

			const currentEntities = entiytIds?.includes(action.stEntityId)
				? [...(state.byId[action.accountId].entities || [])]
				: [...(state.byId[action.accountId].entities || []), action.stEntityId];

			return {
				...state,
				byId: {
					...state.byId,
					[action.accountId]: {
						...state.byId[action.accountId],
						entities: currentEntities
					}
				}
			};
		}
		case types.UNRELATE_ENTITY_ACCOUNT: {
			return {
				...state,
				byId: {
					...state.byId,
					[action.accountId]: {
						...state.byId[action.accountId],
						entities: state.byId[action.accountId].entities.filter((entity) => entity !== action.stEntityId)
					}
				}
			};
		}
		case types.CREATE_ENTITY_FORM: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const accountId = action.objectIds[0];
				const relatedDocument = {
					accountID: accountId,
					id: action.response[0]?.documentID,
					documentName: action.response[0]?.documentName,
					documentTypeID: action.documentTypeId,
					fileExt: 'smt'
				};

				const relatedDocuments =
					state.byId[accountId]?.relatedDocuments && state.byId[accountId]?.relatedDocuments.length > 0
						? [...state.byId[accountId]?.relatedDocuments, relatedDocument]
						: [relatedDocument];

				return {
					...state,
					byId: {
						...state.byId,
						[accountId]: {
							...state.byId[accountId],
							relatedDocuments: relatedDocuments
						}
					}
				};
			}
			return state;
		}
		case types.GET_TOP_ENTITY_METADATA: {
			if (action.response && action.entity && action.entity === entities.Account) {
				const accountId = action.response.id;

				if (state.allIds.includes(accountId) && action.includeAssertions) {
					return {
						...state,
						byId: {
							...state.byId,
							[accountId]: {
								...state.byId[accountId],
								assertions: action.response.assertions
							}
						}
					};
				}
			}

			return state;
		}
		default:
			return state;
	}
}

export function accountsEditReducer(state = initialstate.accounts, action) {
	switch (action.type) {
		case types.GET_ACCOUNTS_EDIT:
			if (action.response) {
				return action.response.data || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function accountAssertionsReducer(state = initialstate.accountAssertions, action) {
	switch (action.type) {
		case types.GET_ASSERTIONS_FOR_ACCOUNTID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function accountDetailsReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.GET_REVIEWS_FOR_ACCOUNTID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function removeAccountReviewReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.DELETE_REVIEWS_FOR_ACCOUNTID:
			return state;
	}
}

export function addAccountReviewReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.ADD_REVIEWS_FOR_ACCOUNTID:
			return action.response;
	}
}

export function currentAccountReducerSEM(state = initialstate.currentAccountSEM, action) {
	switch (action.type) {
		case types.GET_ASSERTIONS_FOR_ACCOUNTID_SEM:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

//TODO: move to a common function in utils.
export function formatAssertionsForDisplayInWcgwTask(assertions, checkedAssertions = true) {
	let assertionsToUpdate = _.cloneDeep(assertions);

	if (assertions && assertions.length === 5) {
		if (checkedAssertions === false) {
			return assertionsToUpdate;
		} else {
			assertionsToUpdate.map((assertion) => {
				assertion.checked = false;
			});
		}

		return assertionsToUpdate;
	} else if (assertions && assertions.length > 0 && assertions.length < 5) {
		if (checkedAssertions === true) {
			assertionsToUpdate.map((assertion) => {
				assertion.checked = false;
			});
		}
		let assertionKnowledgeIds = assertionsToUpdate.map((a) => a.knowledgeAssertionId),
			emptyAssertion = {
				assertionName: '',
				assertionAbbreviation: '',
				accountId: 0,
				assertionId: 0,
				inherentRiskTypeId: 0,
				controlRiskTypeId: 0,
				hasRiskAssociated: false,
				accountName: null,
				knowledgeAssertionId: 6,
				craValue: '',
				validationStatus: null,
				validationMessages: null,
				displayOrder: 0,
				id: 0,
				notForDisplay: true
			};

		if (!assertionKnowledgeIds.some((v) => [6, 1, 10].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(0, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [7, 2, 11].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(1, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [8, 3, 12].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(2, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [4, 13].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(3, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [9, 5, 14].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(4, 0, emptyAssertion);
		}
		return assertionsToUpdate;
	}
}

export function accountReducer(state = initialstate.account, action) {
	let newState;

	switch (action.type) {
		case types.CREATE_MANAGE_ACCOUNT:
		case types.CREATE_ACCOUNT:
			if (action.response && !action.error) {
				newState = _.cloneDeep(state);
				newState = action.response;
				return newState;
			}
			return state;
		case types.GET_ACCOUNT_BY_ID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		case types.CLEAN_ACCOUNT:
			return {};
		case types.UPDATE_ACCOUNT:
		case types.UPDATE_MANAGE_ACCOUNT:
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let account = _.cloneDeep(state);
				if (account.id === action.response.id) {
					account.accountTypeId = action.response.accountTypeId;
				}
				account = {...account, ...action.response};

				return account;
			}
			return state;
		case types.UPDATE_STORE_ACCOUNT_ASSERTION: {
			const account = _.cloneDeep(state);
			account.assertions = action.payload?.map((item) => ({id: item.assertionId}));
			return account;
		}
		case types.DELETE_ACCOUNT:
			return {};
		case types.GET_TOP_ENTITY_METADATA: {
			if (action.response && action.entity && action.entity === entities.Account) {
				return {
					...action.response
				};
			}

			return state;
		}
		default:
			return state;
	}
}

export function areAllCanvasAccountsMappedToHelixAccountsReducer(
	state = initialstate.helixAccountsMappingStatus,
	action
) {
	switch (action.type) {
		case types.GET_IS_HELIX_CANVAS_ACCOUNT_MAPPINGS_SUCCESSFULL:
			return (
				action.response?.allCanvasAccountIncludeInHelixAccount &&
				action.response?.allHelixAccountsIncludeInCanvasAccounts
			);
		default:
			return state;
	}
}
